import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { EditableBlock, EditablePhoto } from './articleBlocks';
import { MASONRY_COLUMN_COPY, MASONRY_COLUMN_OPTIONS, MasonryColumns } from './BlogPhotoPicker';

interface BlogPhotoEditorProps {
  draft: EditableBlock;
  onChange: (draft: EditableBlock) => void;
  onClose: () => void;
  onSave: (draft: EditableBlock) => void;
  onUpload: (files: File[]) => void;
  onChooseGallery: () => void;
}

function BlogPhotoEditor({ draft, onChange, onClose, onSave, onUpload, onChooseGallery }: BlogPhotoEditorProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('.blog-picker-front')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const updatePhoto = (index: number, field: 'alt' | 'caption', value: string) => {
    const photos = draft.photos.map((photo, photoIndex) => (
      photoIndex === index ? { ...photo, [field]: value } : photo
    ));
    onChange({ ...draft, photos });
  };

  const movePhoto = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= draft.photos.length) return;
    const photos = [...draft.photos];
    const [moved] = photos.splice(index, 1);
    photos.splice(next, 0, moved);
    onChange({ ...draft, photos });
  };

  const removePhoto = (index: number) => {
    onChange({ ...draft, photos: draft.photos.filter((_, photoIndex) => photoIndex !== index) });
  };

  const room = Math.max(0, 12 - draft.photos.length);
  const ready = draft.photos.length === 0 || draft.photos.every((photo) => photo.alt.trim());
  const title = draft.kind === 'row' ? 'Edit photo row' : draft.photos.length > 1 ? 'Edit photographs' : 'Edit photograph';
  const showColumns = draft.kind === 'masonry' || draft.kind === 'figure';
  const becomesSingle = draft.kind !== 'figure' && draft.photos.length === 1 && draft.columns === 1;

  const chooseColumns = (columns: MasonryColumns) => {
    const kind = draft.kind === 'row' ? 'row' : (columns === 1 && draft.photos.length === 1 ? 'figure' : 'masonry');
    onChange({ ...draft, columns, kind });
  };

  return createPortal(
    <div className="blog-picker-backdrop blog-photo-editor-backdrop" role="presentation">
      <div className="blog-picker blog-photo-editor" role="dialog" aria-modal="true" aria-label={title}>
        <header className="blog-picker-head">
          <div>
            <p className="blog-picker-kicker">In this article</p>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose}>Close</button>
        </header>
        {showColumns && draft.photos.length ? (
          <fieldset className="blog-arrangement">
            <legend>Columns</legend>
            {MASONRY_COLUMN_OPTIONS.map((value) => (
              <button key={value} type="button" className={draft.columns === value ? 'is-selected' : ''} onClick={() => chooseColumns(value)}>
                {value}
              </button>
            ))}
            <p>{MASONRY_COLUMN_COPY[draft.columns]}</p>
          </fieldset>
        ) : null}
        {becomesSingle ? (
          <p className="blog-picker-note">One photograph is left. Updating will place it on its own, instead of in a group.</p>
        ) : null}
        {!draft.photos.length ? (
          <p className="blog-picker-note">Nothing is left in this group. Updating will remove it from the article.</p>
        ) : (
          <ol className="blog-picker-details">
            {draft.photos.map((photo: EditablePhoto, index) => (
              <li key={`${photo.src}-${index}`}>
                <img src={photo.src} alt="" />
                <label>Description
                  <input value={photo.alt} onChange={(event) => updatePhoto(index, 'alt', event.target.value)} />
                </label>
                <label>Caption, optional
                  <input value={photo.caption} placeholder="Shown under this photograph" onChange={(event) => updatePhoto(index, 'caption', event.target.value)} />
                </label>
                <div className="blog-photo-edit-actions">
                  {draft.photos.length > 1 ? (
                    <>
                      <button type="button" disabled={index === 0} onClick={() => movePhoto(index, -1)}>Move earlier</button>
                      <button type="button" disabled={index === draft.photos.length - 1} onClick={() => movePhoto(index, 1)}>Move later</button>
                    </>
                  ) : null}
                  <button type="button" onClick={() => removePhoto(index)}>Remove</button>
                </div>
              </li>
            ))}
          </ol>
        )}
        {room > 0 ? (
          <div className="blog-photo-add">
            <label>Upload<input type="file" accept="image/*" multiple onChange={(event) => {
              const files = Array.from(event.target.files || []);
              event.target.value = '';
              if (files.length) onUpload(files);
            }} /></label>
            <button type="button" onClick={onChooseGallery}>Choose from galleries</button>
          </div>
        ) : <p className="blog-picker-note">This group already has 12 photographs.</p>}
        {!ready ? <p className="blog-picker-note">Each photograph needs a short description.</p> : null}
        <footer className="blog-picker-foot">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!ready} onClick={() => onSave(draft)}>
            {draft.photos.length ? 'Update article' : 'Remove from article'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default BlogPhotoEditor;
